import { Link, useParams } from "react-router-dom";
import EntityProvider from "pages/components/Behavior/EntityProvider";

import { Box } from "@mui/material";
import {
  getPayment,
  markPaymentAsPayed,
} from "Application/Payment/PaymentService";
import { CreditorPayment, Payment } from "Domain/Payment/Entity/Payment";
import {
  BACKEND_DATE_FORMAT,
  formatPrice,
  getDateFormat,
} from "pages/Utils/Formats";
import moment from "moment";
import Button from "pages/components/FormFields/Button";
import Section from "pages/components/Section";
import Accordion from "pages/components/Accordion";
import PaymentPayOuts from "./Parts/PaymentPayOuts";
import { useState } from "react";
import PaymentFileModal from "pages/components/Molecules/PaymentFileModal";
import { PAYMENT_FILE } from "Application/RequestTypes";
import { getApiUrl } from "Infrastructure/WebClient";
import { eventDispatcher } from "pages/Hooks/useEventListener";
import { TRIGGER_DIALOG } from "pages/constants/EventKeys";
import {
  getUrl,
  USER_DETAIL,
  CREDITOR_PAYMENT_DETAIL,
} from "Infrastructure/config/routesConfig";
import { useTranslation } from "react-i18next";
import PaymentStatusChip from "pages/components/Status/PaymentStatusChip";

export default function PaymentDetails() {
  const { id = "" } = useParams<string>();
  return (
    <EntityProvider<Payment> callback={getPayment} params={[id]}>
      {(payment, setPayment) => (
        <div>
          <Box display={"flex"} justifyContent={"end"} paddingBottom={1}>
            <SetExecutedPayment payment={payment} updatePayment={setPayment} />
          </Box>

          <Box display={"grid"} gap={2} width={"100%"}>
            <PaymentSummery payment={payment} />
            <CreditorPaymentSection payment={payment} />
          </Box>
        </div>
      )}
    </EntityProvider>
  );
}

type PaymentProps = {
  payment: Payment;
};
function PaymentSummery({ payment }: PaymentProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  function displayXML() {
    setOpen(true);
  }
  const xmlFileName =
    moment(payment.date).format(BACKEND_DATE_FORMAT) + "-" + payment.publicId;
  return (
    <Section
      title={"payment_summary"}
      icon={<PaymentStatusChip status={payment.status} />}
    >
      <Box display={"flex"} justifyContent={"space-between"} width={"100%"}>
        <Box fontWeight={"bold"}>
          {moment(payment.date).format(getDateFormat())}
        </Box>

        <Box fontWeight={"bold"} color={"red"}>
          {formatPrice(payment.paymentAmount)}
        </Box>
        <Box>
          <Button
            size="small"
            label={t(`FORM.SUBMIT.file_to_execute`)}
            onClick={displayXML}
          />
          <PaymentFileModal
            open={open}
            xmlUrl={getApiUrl(PAYMENT_FILE, { id: payment.publicId })}
            xmlFileName={xmlFileName}
            setOpen={setOpen}
            tradKey={"payment-file"}
          />
        </Box>
      </Box>
    </Section>
  );
}

function CreditorPaymentSection({ payment }: PaymentProps) {
  return (
    <Section title={"creditor_payments"}>
      <Box width={"100%"}>
        {payment.creditorPayments.map(
          (creditorPayment: CreditorPayment, i: number) => (
            <CreditorPaymentDetail
              key={i}
              expended={i === 0}
              creditorPayment={creditorPayment}
            />
          )
        )}
      </Box>
    </Section>
  );
}

type CreditorPaymentProps = {
  expended?: boolean;
  creditorPayment: CreditorPayment;
};
function CreditorPaymentDetail({
  expended,
  creditorPayment,
}: CreditorPaymentProps) {
  return (
    <Accordion
      defaultExpanded={expended}
      summary={<CreditorPaymentSummary creditorPayment={creditorPayment} />}
      content={<PaymentPayOuts paymentsOut={creditorPayment.paymentsOut} />}
    />
  );
}

function CreditorPaymentSummary({ creditorPayment }: CreditorPaymentProps) {
  return (
    <Box display={"flex"} justifyContent={"space-between"} width={"100%"}>
      <Box fontWeight={"bold"}>
        <Link
          style={{ textDecoration: "none" }}
          to={getUrl(USER_DETAIL, {
            id: creditorPayment.creditorData.publicId,
          })}
        >
          {creditorPayment.creditorData.name}
        </Link>
      </Box>
      <Box marginRight={5}>
        <Link
          style={{ textDecoration: "none", color: "red" }}
          to={getUrl(CREDITOR_PAYMENT_DETAIL, { id: creditorPayment.publicId })}
        >
          {formatPrice(creditorPayment.paymentAmount)}
        </Link>
      </Box>
    </Box>
  );
}

type SessionNextStepProps = {
  payment: Payment;
  updatePayment: Function;
};

function SetExecutedPayment({ payment, updatePayment }: SessionNextStepProps) {
  const { t } = useTranslation();
  if (!(payment.status !== "PAYED")) {
    return <></>;
  }

  function triggerDialog() {
    function triggerPaymentExecution() {
      markPaymentAsPayed(payment.publicId).then((res: any) => {
        if (res && !("error" in res)) {
          updatePayment(res);
        }
      });
    }
    const actions = [
      {
        label: "confirm",
        variant: "outlined",
        color: "success",
        action: triggerPaymentExecution,
      },
      {
        label: "cancel",
        variant: "outlined",
        color: "error",
      },
    ];
    eventDispatcher(TRIGGER_DIALOG, {
      type: "executed_payment",
      actions: actions,
    });
  }

  return (
    <>
      <Button label={t(`FORM.SUBMIT.mark_executed`)} onClick={triggerDialog} />
    </>
  );
}
